import {
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  ArrowsIn,
  FrameCorners,
  Selection,
} from "@phosphor-icons/react";
import { DropdownMenu } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEditor, useValue } from "tldraw";

export function ZoomMenuContent() {
  const { t } = useTranslation("ui");
  const editor = useEditor();
  const zoom = useValue("zoom level", () => editor.getZoomLevel(), [editor]);
  const isZoom100 = Math.abs(zoom - 1) < 0.01;

  const [lastFitZoom, setLastFitZoom] = useState<number | null>(null);

  const handleZoomIn = (e: Event) => {
    e.preventDefault();
    editor.zoomIn();
  };

  const handleZoomOut = (e: Event) => {
    e.preventDefault();
    editor.zoomOut();
  };

  const handleResetZoom = (e: Event) => {
    e.preventDefault();
    editor.resetZoom();
  };

  const handleZoomToFit = (e: Event) => {
    e.preventDefault();
    editor.zoomToFit();
    setLastFitZoom(editor.getZoomLevel());
  };

  const handleZoomToSelection = (e: Event) => {
    e.preventDefault();
    editor.zoomToSelection();
  };

  const isZoomToFit = Boolean(
    lastFitZoom && Math.abs(zoom - lastFitZoom) < 0.01,
  );

  return (
    <>
      <DropdownMenu.Item
        className="tlui-menu__item hover:bg-gray-100 flex items-center gap-2 rounded-lg px-3 py-1.5 hover:text-puntt-cool-gray-dark-1"
        onSelect={handleZoomIn}
        data-testid="zoom-menu-zoom-in"
      >
        <MagnifyingGlassPlus className="size-4" />
        <span className="!text-xs">{t("tickets.zoomMenuContent.zoom")}</span>
      </DropdownMenu.Item>
      <DropdownMenu.Item
        className="tlui-menu__item hover:bg-gray-100 flex items-center gap-2 rounded-lg px-3 py-1.5 hover:text-puntt-cool-gray-dark-1"
        onSelect={handleZoomOut}
        data-testid="zoom-menu-zoom-out"
      >
        <MagnifyingGlassMinus className="size-4" />
        <span className="!text-xs">{t("tickets.zoomMenuContent.zoomOut")}</span>
      </DropdownMenu.Item>
      <DropdownMenu.Item
        className={cx(
          "tlui-menu__item hover:bg-gray-100 flex items-center gap-2 rounded-lg px-3 py-1.5 hover:text-puntt-cool-gray-dark-1",
          isZoom100 && "cursor-not-allowed opacity-50",
        )}
        onSelect={handleResetZoom}
        disabled={isZoom100}
        data-testid="zoom-menu-reset"
      >
        <ArrowsIn className="size-4" />
        <span className="!text-xs">{t("tickets.zoomMenuContent.zoom100")}</span>
      </DropdownMenu.Item>
      <DropdownMenu.Item
        className={cx(
          "tlui-menu__item hover:bg-gray-100 flex items-center gap-2 rounded-lg px-3 py-1.5 hover:text-puntt-cool-gray-dark-1",
          isZoomToFit ? "cursor-not-allowed opacity-50" : "hover:bg-gray-100",
        )}
        onSelect={handleZoomToFit}
        disabled={isZoomToFit}
        data-testid="zoom-menu-fit"
      >
        <FrameCorners className="size-4" />
        <span className="!text-xs">{t("tickets.zoomMenuContent.zoomFit")}</span>
      </DropdownMenu.Item>
      <DropdownMenu.Item
        className="tlui-menu__item hover:bg-gray-100 flex items-center gap-2 rounded-lg px-2 py-1 hover:text-puntt-cool-gray-dark-1"
        onSelect={handleZoomToSelection}
        disabled={!editor.getSelectedShapeIds().length}
        data-testid="zoom-menu-selection"
      >
        <Selection className="size-4" />
        <span className="!text-xs">
          {t("tickets.zoomMenuContent.zoomSelection")}
        </span>
      </DropdownMenu.Item>
    </>
  );
}
